import React, { useEffect, useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  Typography,
  Box,
} from '@mui/material';
import { useGlobalContext } from '../../context/GlobalStore';
import { styles } from '../Account/styles';
import Loading from '../Loading';
import ApiKeySelect from '../ApiKeySelect';

const ChangeApiKeyModal = (props) => {
  const {
    open,
    handleClose,
    handleMoveApiKey,
    currentApiKey,
    newApiKey,
    setNewApiKey,
    isLoading,
  } = props;
  const { state } = useGlobalContext();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [currentKeyId, setCurrentKeyId] = useState('');

  // Find the API key ID that corresponds to the current API key name
  useEffect(() => {
    if (state.account && state.account.pylo_accounts) {
      const currentAccount = state.account.pylo_accounts.find(
        (account) => account.name === currentApiKey
      );
      
      if (currentAccount) {
        setCurrentKeyId(currentAccount.key_id);
        setNewApiKey({
          api_key_id: currentAccount.key_id
        });
      }
    }
  }, [state.account, currentApiKey, setNewApiKey]);

  // Check if a new API key has been selected
  const isNewKeySelected = newApiKey.api_key_id && newApiKey.api_key_id !== currentKeyId;

  const handleChange = (value) => {
    setNewApiKey({
      api_key_id: value,
    });
  };

  const handleEdit = (e) => {
    e.preventDefault();
    setShowConfirmation(true);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    setShowConfirmation(false);
    handleMoveApiKey(e);
    handleClose();
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  // Find the name of the selected API key for display in confirmation
  const getSelectedKeyName = () => {
    if (!state.account || !newApiKey.api_key_id) return '';
    
    const selectedAccount = state.account.pylo_accounts.find(
      (account) => account.key_id === newApiKey.api_key_id
    );
    
    return selectedAccount ? selectedAccount.name : '';
  };

  const closeModal = () => {
    setShowConfirmation(false);
    const currentAccount = state.account.pylo_accounts.find(
      (account) => account.name === currentApiKey
    );
    
    if (currentAccount) {
      setNewApiKey({
        api_key_id: currentAccount.key_id
      });
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      maxWidth="sm"
      onSubmit={handleMoveApiKey}
      component="form"
    >
      {isLoading ? (
        <DialogContent sx={styles.loadingBox}>
          <Loading />
        </DialogContent>
      ) : (
        <>
          <DialogTitle>{showConfirmation ? "Are you sure?" : "API Key Assignment"}</DialogTitle>
          <DialogContent>
            {showConfirmation ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0, fontSize: '1rem' }}>
                  This device will be reassigned to <Typography component="span" fontStyle="italic" display="inline">{getSelectedKeyName()}</Typography> and will no longer be available in <Typography component="span" fontStyle="italic" display="inline">{currentApiKey}</Typography>.
                </Typography>
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                    Devices are only available on one API key at a time. Changing the assignment allows you to make this device available under a different key.
                  </Typography>
                  <Typography variant="body2" fontStyle="italic" color="text.secondary" mt={2} sx={{ fontSize: '1rem' }}>
                    Readings do not move with the device, but can still be accessed using the API Key the device was assigned to when they were received.
                  </Typography>
                </Box>
                <Box mb={1} sx={{ width: '100%' }}>
                  <ApiKeySelect
                    account={state.account}
                    value={newApiKey.api_key_id}
                    onChange={handleChange}
                    label=""
                    displayLabel={false}
                    fullWidth={true}
                    sx={{ width: '100%' }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: 1 }}>
            {showConfirmation ? (
              <>
                <Button onClick={handleCancel} variant="outlined">
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleConfirm} color="primary" autoFocus>
                  Confirm
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleEdit} autoFocus disabled={!isNewKeySelected}>
                  Update
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ChangeApiKeyModal;
